<template>
    <Transition name="sheet">
        <div v-if="event" class="sheet-overlay" @click.self="$emit('close')">
            <div class="sheet">
                <div class="sheet-handle"></div>
                <div class="sheet-color" :style="{ background: event.color || null }"></div>

                <h2 class="sheet-title">{{ event.name }}</h2>
                <div class="sheet-datetime">{{ dateTimeLabel }}</div>

                <div v-if="event.room_name" class="sheet-row">
                    <i class="fa-solid fa-location-dot"></i>
                    <span class="sheet-row-label">{{ $t('room') }}</span>
                    <span class="sheet-row-value">{{ event.room_name }}</span>
                </div>
                <div v-if="event.trainer_name && event.trainer_name.trim()" class="sheet-row">
                    <i class="fa-solid fa-user-tie"></i>
                    <span class="sheet-row-label">{{ $t('trainer') }}</span>
                    <span class="sheet-row-value">{{ event.trainer_name }}</span>
                </div>

                <div class="sheet-row" style="border-bottom: none; display: block;">
                    <div style="display:flex; align-items:center; gap:12px;">
                        <i class="fa-solid fa-users"></i>
                        <span class="sheet-row-label">{{ $t('spots') }}</span>
                        <span class="sheet-row-value">{{ spotsLabel }}</span>
                    </div>
                    <div v-if="event.capacity > 0" class="capacity">
                        <div class="capacity-track">
                            <div :class="['capacity-fill', fillClass]" :style="{ width: fillWidth }"></div>
                        </div>
                        <div class="capacity-text">
                            <span>{{ $t('signedUpCount') }}: {{ event.attendance }}</span>
                            <span>{{ event.capacity }}</span>
                        </div>
                    </div>
                </div>

                <div class="sheet-actions">
                    <button v-if="event.signed_up && !event.past" class="btn btn-danger btn-block btn-lg" @click="$emit('cancel', event)">
                        <i class="fa-solid fa-xmark"></i> {{ $t('cancelSignup') }}
                    </button>
                    <button v-else-if="event.on_waiting_list && !event.past" class="btn btn-danger btn-block btn-lg" @click="$emit('waitlist-leave', event)">
                        <i class="fa-solid fa-xmark"></i> {{ $t('leaveWaitingList') }}
                    </button>
                    <button v-else-if="canSignUp" class="btn btn-primary btn-block btn-lg" @click="$emit('signup', event)">
                        <i class="fa-solid fa-check"></i> {{ $t('signUp') }}
                    </button>
                    <button v-else-if="canJoinWaitlist" class="btn btn-primary btn-block btn-lg" @click="$emit('waitlist-join', event)">
                        <i class="fa-solid fa-hourglass-half"></i> {{ $t('joinWaitingList') }}
                    </button>

                    <div v-if="event.signed_up" class="sheet-note signed">
                        <i class="fa-solid fa-circle-check"></i> {{ $t('signedUp') }}
                    </div>
                    <div v-else-if="event.on_waiting_list" class="sheet-note warn">
                        <i class="fa-solid fa-hourglass-half"></i> {{ waitingPositionLabel }}
                    </div>
                    <div v-else-if="event.past" class="sheet-note">{{ $t('sessionPassed') }}</div>
                    <div v-else-if="event.registration_closed" class="sheet-note warn">
                        <i class="fa-solid fa-lock"></i> {{ $t('registrationClosed') }}
                    </div>
                    <div v-else-if="event.requires_membership && !event.membership_ok" class="sheet-note warn">
                        <i class="fa-solid fa-triangle-exclamation"></i> {{ $t('membershipRequired') }}
                    </div>
                    <div v-else-if="event.full" class="sheet-note warn">{{ $t('full') }}</div>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script>
import moment from 'moment';

export default {
    name: 'SessionSheet',
    props: {
        event: {
            type: Object,
            default: null
        }
    },
    emits: ['close', 'signup', 'cancel', 'waitlist-join', 'waitlist-leave'],
    computed: {
        canSignUp() {
            const e = this.event;
            if (!e || e.signed_up || e.on_waiting_list || e.past || e.registration_closed || e.full) return false;
            if (e.requires_membership && !e.membership_ok) return false;
            return true;
        },
        canJoinWaitlist() {
            // Only when the session is full (otherwise they simply sign up), membership is OK, sign-ups have not
            // closed and it hasn't passed.
            const e = this.event;
            if (!e || e.signed_up || e.on_waiting_list || e.past || e.registration_closed || !e.full) return false;
            if (e.requires_membership && !e.membership_ok) return false;
            return true;
        },
        waitingPositionLabel() {
            if (!this.event || !this.event.waiting_position) return this.$t('onWaitingList');
            return this.$t('waitingListPosition').replace('%n%', this.event.waiting_position);
        },
        dateTimeLabel() {
            if (!this.event) return '';
            const lang = this.$store.state.language; // localise + recompute on language change
            const start = moment(this.event.start, 'YYYY-MM-DD HH:mm:ss').locale(lang);
            const end = moment(this.event.end, 'YYYY-MM-DD HH:mm:ss').locale(lang);
            let label = start.format('dddd, D. MMMM') + ' · ' + start.format('HH:mm');
            if (end.isValid()) label += '–' + end.format('HH:mm');
            return label;
        },
        spotsLabel() {
            if (!this.event) return '';
            if (this.event.capacity <= 0) return this.$t('unlimited');
            const free = Math.max(0, this.event.capacity - this.event.attendance);
            return free + ' / ' + this.event.capacity + ' ' + this.$t('freeSpots');
        },
        fillWidth() {
            if (!this.event || this.event.capacity <= 0) return '0%';
            const ratio = Math.min(1, this.event.attendance / this.event.capacity);
            return Math.round(ratio * 100) + '%';
        },
        fillClass() {
            if (!this.event || this.event.capacity <= 0) return '';
            const ratio = this.event.attendance / this.event.capacity;
            if (ratio >= 1) return 'full';
            if (ratio >= 0.75) return 'high';
            return '';
        }
    }
};
</script>

<style scoped>
.sheet-note.signed {
    color: #5aa863;
}

.sheet-note.signed i {
    margin-right: 4px;
}
</style>
